import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@/i18n'
import {
  ToastContext,
  type Toast,
  type ToastContextValue,
  type ToastShowOptions,
} from './ToastContext'

/**
 * Toast (操作完了通知) — Provider + Portal。
 * Context / 型定義は `./ToastContext` に分離 (Vite Fast Refresh の制約上、
 * Component 以外の export を Provider ファイルから外すため)。
 *
 * 設計判断 (T-B5):
 *   - **インスタンス型**: state は React Provider 内に閉じ込め、L-15 教訓どおり
 *     module-level mutable state を使わない。複数 Provider 共存可。
 *   - **ID 採番**: `crypto.randomUUID()` を使い、衝突しない (module-level counter
 *     を持たない)。Phase 4 でグローバル ID 必要時のみ集約を検討。
 *   - **Portal**: `#toast-root` div を Provider 内で create / cleanup。アプリ
 *     root の DOM 階層から独立させ、stacking context を切る。
 *   - **タイマー所有権**: 各 toast の auto-dismiss timer を Map<id, timerId> で
 *     保持。`dismiss(id)` および unmount 時にすべて clear (leak 防止)。
 *   - **ttlMs=0 で永続表示**: タイマー起動せず、明示的 `dismiss(id)` まで残す
 *     (危険系メッセージで使用想定)。
 *   - **i18n**: `titleKey` / `descKey` は i18n key を受け取り、Item 内で `t()` を
 *     呼ぶ。useTranslation の subscribe で言語切替時にも追従。
 *
 * useToast (`./useToast`) は ToastContext を `null` チェックして throw する。
 * Provider 外で呼ぶと開発者エラーとして即座に検出される。
 *
 * **stability 保証 (consumer の useEffect 依存に関わる契約)**:
 *   - `show` / `dismiss` は `useCallback([])` で stable (toast lifetime 中に identity 不変)。
 *   - `value` は `useMemo([show, dismiss])` で stable。
 *   ⇒ consumer は `useEffect(() => toast.show(...), [state, toast])` のように
 *      `toast` を依存配列に含めても無限ループにならない。
 *   依存例: `pages/LoginPage.tsx` の error effect (R-6)。本契約を破る変更
 *   (例: `value` の再生成、`show` の依存追加) は consumer 側の useEffect 連鎖
 *   再発火を引き起こすため、ToastProvider のリファクタ時はここを必ず維持すること。
 *
 * **dedupe** (旧 L-19 / T-C1 R-5):
 *   - `show({ dedupe: true })` で同 `titleKey` の toast が既に stack に存在すれば
 *     skip して既存 id を返す。連続 401 等で同一エラーメッセージを重ねない用途。
 *   - `show` の stable 契約を維持するため、reducer 状態を `toastsRef` で同期 mirror し、
 *     dedupe 判定でその ref を参照する。`useReducer` の state を直接 closure で見ると
 *     `useCallback([])` が初回 state を捕まえてしまうため使えない。
 *   - 既定は `false` (常に新規追加)。既存 caller の挙動は変えない。
 */

const TOAST_DEFAULT_TTL_MS = 5_000
const TOAST_ROOT_ID = 'toast-root'

type ToastAction = { type: 'add'; toast: Toast } | { type: 'remove'; id: string }

const reducer = (state: readonly Toast[], action: ToastAction): readonly Toast[] => {
  switch (action.type) {
    case 'add':
      return [...state, action.toast]
    case 'remove':
      return state.filter(t => t.id !== action.id)
  }
}

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  // 旧ブラウザ向けフォールバック (Phase 3 動作環境では到達しない想定)
  return `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

export interface ToastProviderProps {
  children: ReactNode
}

export const ToastProvider = ({ children }: ToastProviderProps): JSX.Element => {
  const [toasts, dispatch] = useReducer(reducer, [] as readonly Toast[])
  const timersRef = useRef<Map<string, ReturnType<typeof globalThis.setTimeout>>>(new Map())

  // dedupe 判定で参照する state mirror。`show` を stable に保ったまま
  // 最新 stack を見るために、reducer 更新を同期で ref に反映する。
  const toastsRef = useRef<readonly Toast[]>([])

  const removeFromRef = useCallback((id: string): void => {
    toastsRef.current = toastsRef.current.filter(t => t.id !== id)
  }, [])

  const dismiss = useCallback(
    (id: string): void => {
      const timer = timersRef.current.get(id)
      if (timer !== undefined) {
        globalThis.clearTimeout(timer)
        timersRef.current.delete(id)
      }
      removeFromRef(id)
      dispatch({ type: 'remove', id })
    },
    [removeFromRef],
  )

  const show = useCallback(
    (opts: ToastShowOptions): string => {
      // dedupe: 同 titleKey が既に stack 内に居れば既存 id を返す。
      if (opts.dedupe === true) {
        const existing = toastsRef.current.find(t => t.titleKey === opts.titleKey)
        if (existing) return existing.id
      }

      const id = generateId()

      const toast: Toast = {
        id,
        tone: opts.tone ?? 'info',
        titleKey: opts.titleKey,
        titleVars: opts.titleVars,
        descKey: opts.descKey,
        descVars: opts.descVars,
      }

      toastsRef.current = [...toastsRef.current, toast]
      dispatch({ type: 'add', toast })

      const ttl = opts.ttlMs ?? TOAST_DEFAULT_TTL_MS
      if (ttl > 0) {
        const timer = globalThis.setTimeout(() => {
          timersRef.current.delete(id)
          removeFromRef(id)
          dispatch({ type: 'remove', id })
        }, ttl)

        timersRef.current.set(id, timer)
      }

      return id
    },
    [removeFromRef],
  )

  // unmount 時に進行中タイマーを全 clear (leak 防止)
  useEffect(() => {
    const map = timersRef.current

    return (): void => {
      for (const t of map.values()) globalThis.clearTimeout(t)
      map.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

interface ToastViewportProps {
  toasts: readonly Toast[]
  onDismiss: (id: string) => void
}

const ToastViewport = ({ toasts, onDismiss }: ToastViewportProps): JSX.Element | null => {
  const [root, setRoot] = useState<HTMLElement | null>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (typeof document === 'undefined') return
    const existing = document.getElementById(TOAST_ROOT_ID)
    if (existing) {
      setRoot(existing)

      return
    }
    const created = document.createElement('div')

    created.id = TOAST_ROOT_ID
    document.body.appendChild(created)
    setRoot(created)

    return (): void => {
      // 他の Provider が同じ root を共有していないとき (子要素が空) のみ削除
      if (created.childElementCount === 0) created.remove()
    }
  }, [])

  if (!root) return null

  return createPortal(
    <div className="toast-stack" role="region" aria-label={t('common.toast.region_label')}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    root,
  )
}

interface ToastItemProps {
  toast: Toast
  onDismiss: (id: string) => void
}

const ToastItem = ({ toast, onDismiss }: ToastItemProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <div
      className={`toast toast-${toast.tone}`}
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      data-toast-id={toast.id}
    >
      <div className="toast-body">
        <div className="toast-title">{t(toast.titleKey, toast.titleVars)}</div>
        {toast.descKey != null && (
          <div className="toast-desc">{t(toast.descKey, toast.descVars)}</div>
        )}
      </div>
      <button
        type="button"
        className="toast-dismiss"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('common.ui.dismiss')}
      >
        ×
      </button>
    </div>
  )
}
